import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MoreHorizontal, X } from 'lucide-react';
import { Drawer } from '../../components/ui/Drawer';
import { RefundPaymentDialog } from '../../components/RefundPaymentDialog';
import { PaymentRefundHistory, PaymentRefundTotals } from '../../components/PaymentRefundHistory';
import { useVoidPayment } from '../../hooks/useVoidPayment';
import { receiptErrorMessage, useSendPaymentReceipt } from '../../hooks/useSendPaymentReceipt';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useBusinessContext } from '../../context/BusinessContext';
import { REFUND_PAYMENT, VOID_PAYMENT } from '../../utils/capabilities';
import { isVoidedPayment, refundBlock } from '../../utils/refunds';
import {
  buildVoidRequest, canVoidPayment, formatCardNumber, formatCurrency, formatPaymentDate, gatewayLabel, paymentBusinessId, paymentChipOf,
} from '../../utils/paymentUtils';
import { passDetailLink } from '../../api/businessAccessCombined';
import type { Payment } from '../../types/payment.types';
import { Avatar, StatusChip } from './ActivityControls';
import { TX_CHIP_LABEL, TX_CHIP_TONE, initials } from './activityViews';
import type { Tone } from './activity';

/**
 * SAM-1186 — the two drawers of Activity (canvas v214), in the style of Merchants: the amount large (struck through
 * when Voided), the linked record as a row with its own status, one main button and a «···» menu.
 */

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-3 border-0 border-b border-solid border-[var(--line-subtle)] last:border-b-0 text-sm">
    <span className="text-[var(--ink-500)]">{label}</span>
    <span className="font-semibold text-[var(--ink-900)] text-right break-words min-w-0">{children}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col">
    <h3 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--ink-500)]">{title}</h3>
    {children}
  </section>
);

const CloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <button type="button" aria-label="Close" onClick={onClose} className="h-9 w-9 rounded-lg border-0 bg-transparent cursor-pointer text-[var(--ink-500)] flex items-center justify-center">
    <X size={18} />
  </button>
);

export interface MenuItem { key: string; label: string; onSelect: () => void; danger?: boolean }

/** The «···» menu: closes on outside click and Escape. Renders nothing when there is nothing to offer. */
const MoreMenu: React.FC<{ items: MenuItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (!items.length) return null;
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="h-11 w-11 rounded-xl border border-solid border-[var(--line)] bg-white cursor-pointer flex items-center justify-center text-[var(--ink-700)]">
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 bottom-12 z-10 min-w-40 rounded-xl border border-solid border-[var(--line)] bg-white shadow-lg py-1">
          {items.map((it) => (
            <button key={it.key} type="button" role="menuitem" onClick={() => { setOpen(false); it.onSelect(); }}
              className={`w-full text-left h-10 px-4 border-0 bg-transparent cursor-pointer text-sm font-semibold ${it.danger ? 'text-[var(--bad-fg)]' : 'text-[var(--ink-900)]'}`}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const mainButton = 'flex-1 h-11 rounded-xl border-0 bg-[var(--tenant-primary,#005488)] text-white text-sm font-semibold cursor-pointer inline-flex items-center justify-center no-underline';

/* ---------------- Pass ---------------- */

export interface PassDrawerData {
  id: string;
  guest: string;
  /** «Business pass · Api Beach». */
  subtitle: string;
  type: string;
  destination: string;
  valid: string;
  guests: string;
  created: string;
  status: { label: string; tone: Tone };
  /** The pass's charge, when it has one. */
  payment: Payment | null;
  cancellable: boolean;
}

const LinkedRow: React.FC<{ title: string; sub: string; chip: React.ReactNode; onClick: () => void }> = ({ title, sub, chip, onClick }) => (
  <button type="button" onClick={onClick}
    className="w-full text-left flex items-center gap-3 rounded-xl border border-solid border-[var(--line)] bg-white px-4 py-3 cursor-pointer">
    <span className="flex-1 min-w-0 flex flex-col">
      <span className="text-lg font-bold tabular-nums text-[var(--ink-900)]">{title}</span>
      <span className="text-[13px] text-[var(--ink-500)]">{sub}</span>
    </span>
    {chip}
    <ChevronRight size={16} className="text-[var(--ink-400)]" aria-hidden />
  </button>
);

export const ActivityPassDrawer: React.FC<{
  pass: PassDrawerData | null;
  onClose: () => void;
  onOpenPayment: (p: Payment) => void;
  onCancel: (passId: string) => void;
  cancelling?: boolean;
  cancelError?: string | null;
}> = ({ pass, onClose, onOpenPayment, onCancel, cancelling, cancelError }) => {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { setConfirming(false); }, [pass?.id]);
  if (!pass) return null;
  const pay = pass.payment;
  const chip = pay ? paymentChipOf(pay) : null;
  return (
    <Drawer isOpen onClose={onClose} width="460px">
      <div className="flex flex-col h-full" role="dialog" aria-label="Pass">
        <div className="flex items-center gap-3 p-5 border-0 border-b border-solid border-[var(--line-subtle)]">
          <Avatar initials={initials(pass.guest)} />
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold truncate">{pass.guest}</div>
            <div className="text-[13px] text-[var(--ink-500)] truncate">{pass.subtitle}</div>
          </div>
          <StatusChip tone={pass.status.tone} label={pass.status.label} />
          <CloseButton onClose={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
          <Section title="Details">
            <Row label="Type">{pass.type}</Row>
            <Row label="Destination">{pass.destination}</Row>
            <Row label="Valid">{pass.valid}</Row>
            <Row label="Guests">{pass.guests}</Row>
            <Row label="Created">{pass.created}</Row>
          </Section>
          {pay && (
            <Section title="Payment">
              <LinkedRow
                title={formatCurrency(pay.payment_amount, pay.currency)}
                sub={`${formatCardNumber(pay.cardLast4)} · ${gatewayLabel(pay.payment_gateway)}`}
                chip={chip ? <StatusChip tone={TX_CHIP_TONE[chip]} label={TX_CHIP_LABEL[chip]} /> : null}
                onClick={() => onOpenPayment(pay)}
              />
            </Section>
          )}
          {confirming && (
            <div role="alert" className="rounded-xl border border-solid border-[var(--line)] bg-[var(--warn-bg-2)] p-4 text-[13px] text-[var(--warn-fg)] flex flex-col gap-3">
              {cancelError && <span className="font-semibold text-[var(--bad-fg)]">{cancelError}</span>}
              <span>{pay ? 'Cancel this pass? Its payment will be voided too.' : 'Cancel this pass?'}</span>
              <span className="flex gap-2">
                <button type="button" disabled={cancelling} onClick={() => onCancel(pass.id)} className="h-9 px-4 rounded-lg border-0 bg-[var(--bad-fg)] text-white text-sm font-semibold cursor-pointer disabled:opacity-50">
                  {cancelling ? 'Cancelling…' : 'Cancel pass'}
                </button>
                <button type="button" disabled={cancelling} onClick={() => setConfirming(false)} className="h-9 px-4 rounded-lg border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer">
                  Keep it
                </button>
              </span>
            </div>
          )}
        </div>
        <div className="p-5 flex gap-2 border-0 border-t border-solid border-[var(--line-subtle)]">
          <Link to={passDetailLink(pass.id)} className={mainButton} onClick={onClose}>Open pass</Link>
          <MoreMenu items={pass.cancellable ? [{ key: 'cancel', label: 'Cancel pass', danger: true, onSelect: () => setConfirming(true) }] : []} />
        </div>
      </div>
    </Drawer>
  );
};

/* ---------------- Payment ---------------- */

export interface PaymentDrawerContext {
  title: string;
  guest?: string;
  service?: string;
  /** The pass (or booking) this payment paid for, with its own status. */
  linked?: { kind: 'pass' | 'booking'; id: string; name: string; when: string; status: { label: string; tone: Tone } } | null;
}

export const ActivityPaymentDrawer: React.FC<{
  payment: Payment | null;
  context?: PaymentDrawerContext;
  businessId: string;
  onClose: () => void;
  onOpenPass?: (passId: string) => void;
}> = ({ payment, context, businessId, onClose, onOpenPass }) => {
  const voidMutation = useVoidPayment();
  const receipt = useSendPaymentReceipt();
  const { hasCapability, isLoading: capsLoading } = useCapabilities();
  const { currentBusinessId } = useBusinessContext();
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [refunding, setRefunding] = useState<Payment | null>(null);
  useEffect(() => { setConfirmVoid(false); receipt.reset(); }, [payment?.transaction_reference]); // eslint-disable-line react-hooks/exhaustive-deps

  // The refund dialog outlives the drawer (it opens after the drawer closes).
  const refundDialog = (
    <RefundPaymentDialog
      isOpen={!!refunding}
      payment={refunding}
      businessId={refunding?.business_id || businessId || null}
      onClose={() => setRefunding(null)}
    />
  );
  if (!payment) return refundDialog;

  const chip = isVoidedPayment(payment) ? 'voided' : paymentChipOf(payment);
  // Void and Refund are never offered together: inside the void window the payment is voided; past it, only refunded.
  const showVoid = canVoidPayment(payment) && !capsLoading && hasCapability(VOID_PAYMENT, paymentBusinessId(payment, currentBusinessId));
  const refundBusinessId = payment.business_id || currentBusinessId;
  const blocked = refundBlock(payment);
  const showRefund = !showVoid && !blocked && !capsLoading && !!refundBusinessId && hasCapability(REFUND_PAYMENT, refundBusinessId);
  const hasRefundData = payment.refunded_amount !== undefined || (payment.refunds && payment.refunds.length > 0) || !!payment.refund_status;

  const items: MenuItem[] = [
    ...(showVoid ? [{ key: 'void', label: 'Void', danger: true, onSelect: () => setConfirmVoid(true) }] : []),
    ...(showRefund ? [{ key: 'refund', label: 'Refund', onSelect: () => { const p = payment; onClose(); setRefunding(p); } }] : []),
  ];

  const doVoid = () => {
    const request = buildVoidRequest(payment, businessId);
    if (request) voidMutation.mutate(request, { onSuccess: onClose });
  };

  // A receipt exists for a charge that went through, voided or refunded since; never for a declined or pending one.
  const canSendReceipt = !!payment.transaction_reference && chip !== 'failed' && !!chip;
  const sendReceipt = () => {
    const business_id = payment.business_id || businessId;
    if (payment.transaction_reference && business_id) receipt.mutate({ transaction_reference: payment.transaction_reference, business_id });
  };

  const linked = context?.linked;
  return (
    <>
      <Drawer isOpen onClose={onClose} width="460px">
        <div className="flex flex-col h-full" role="dialog" aria-label="Payment details">
          <div className="flex items-center gap-3 p-5 border-0 border-b border-solid border-[var(--line-subtle)]">
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold">Payment details</div>
              <div className="text-[13px] text-[var(--ink-500)] truncate">{context?.title ?? 'Payment'}</div>
            </div>
            <CloseButton onClose={onClose} />
          </div>
          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
            <div className="flex flex-col items-start gap-2">
              <span className={`text-4xl font-bold tabular-nums ${chip === 'voided' ? 'line-through text-[var(--ink-400)]' : 'text-[var(--ink-900)]'}`}>
                {formatCurrency(payment.payment_amount, payment.currency)}
              </span>
              {chip && <StatusChip tone={TX_CHIP_TONE[chip]} label={TX_CHIP_LABEL[chip]} />}
              <span className="text-[13px] text-[var(--ink-500)]">{formatPaymentDate(payment.payment_date || payment.createdAt)}</span>
            </div>
            <Section title="Details">
              {context?.guest && <Row label="Guest">{context.guest}</Row>}
              {context?.service && <Row label="Service">{context.service}</Row>}
              <Row label="Gateway">{gatewayLabel(payment.payment_gateway)}</Row>
              <Row label="Card">{formatCardNumber(payment.cardLast4)}</Row>
              <Row label="Reference"><span className="font-mono">{payment.payment_reference || '—'}</span></Row>
            </Section>
            {hasRefundData && (
              <section className="flex flex-col gap-3">
                <PaymentRefundTotals payment={payment} />
                <PaymentRefundHistory payment={payment} />
              </section>
            )}
            {blocked === 'unresolved' && (
              <div role="alert" data-testid="refund-unresolved" className="rounded-xl bg-[var(--warn-bg-2)] text-[var(--warn-fg)] p-4 text-[13px]">
                A refund on this payment is pending or needs reconciliation. Do not send another one until it is resolved.
              </div>
            )}
            {linked && (
              <Section title={linked.kind === 'pass' ? 'Pass' : 'Booking'}>
                <LinkedRow
                  title={linked.name}
                  sub={linked.when}
                  chip={<StatusChip tone={linked.status.tone} label={linked.status.label} />}
                  onClick={() => { if (linked.kind === 'pass' && onOpenPass) onOpenPass(linked.id); }}
                />
              </Section>
            )}
            {receipt.isSuccess && (
              <div role="status" className="rounded-xl bg-[var(--ok-bg)] text-[var(--ok-fg)] p-4 text-[13px]">Receipt sent by SMS to the guest.</div>
            )}
            {receipt.isError && (
              <div role="alert" className="rounded-xl bg-[var(--bad-bg)] text-[var(--bad-fg)] p-4 text-[13px]">{receiptErrorMessage(receipt.error)}</div>
            )}
            {confirmVoid && (
              <div role="alert" className="rounded-xl bg-[var(--warn-bg-2)] text-[var(--warn-fg)] p-4 text-[13px] flex flex-col gap-3">
                <span>
                  Void this payment?{payment.business_access_id ? ' The pass of this payment will be cancelled too.' : ''} This cannot be undone.
                </span>
                <span className="flex gap-2">
                  <button type="button" disabled={voidMutation.isPending} onClick={doVoid} className="h-9 px-4 rounded-lg border-0 bg-[var(--bad-fg)] text-white text-sm font-semibold cursor-pointer disabled:opacity-50">
                    {voidMutation.isPending ? 'Voiding…' : 'Void'}
                  </button>
                  <button type="button" disabled={voidMutation.isPending} onClick={() => setConfirmVoid(false)} className="h-9 px-4 rounded-lg border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer">
                    Keep it
                  </button>
                </span>
              </div>
            )}
          </div>
          {(items.length > 0 || canSendReceipt || (linked?.kind === 'pass' && onOpenPass)) && (
            <div className="p-5 flex gap-2 border-0 border-t border-solid border-[var(--line-subtle)]">
              {canSendReceipt ? (
                <button type="button" className={mainButton} disabled={receipt.isPending} onClick={sendReceipt}>
                  {receipt.isPending ? 'Sending…' : 'Send receipt by SMS'}
                </button>
              ) : linked?.kind === 'pass' && onOpenPass ? (
                <button type="button" className={mainButton} onClick={() => onOpenPass(linked.id)}>View pass</button>
              ) : <span className="flex-1" />}
              <MoreMenu items={items} />
            </div>
          )}
        </div>
      </Drawer>
      {refundDialog}
    </>
  );
};
